import { useState } from 'react';
import { User, Crown, HelpCircle, Download, LogOut, CreditCard, ExternalLink, Gift, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import LiveChatModal from '@/components/chat/LiveChatModal';
import EmailSupportModal from '@/components/chat/EmailSupportModal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { AccountSection } from '@/components/voyager/AccountSection';
import { PaymentSection } from '@/components/voyager/PaymentSection';
import { SubscriptionSection } from '@/components/voyager/SubscriptionSection';
import { HelpSection } from '@/components/voyager/HelpSection';

interface VoyagerDropdownProps {
  children: React.ReactNode;
}

export function VoyagerDropdown({ children }: VoyagerDropdownProps) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { toast } = useToast();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isPaymentsOpen, setIsPaymentsOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadApp = () => {
    const userAgent = navigator.userAgent || navigator.vendor;
    
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      window.open('https://apps.apple.com/app/odyvoy', '_blank');
    } else if (/android/i.test(userAgent)) {
      window.open('https://play.google.com/store/apps/details?id=app.lovable.odyvoy', '_blank');
    } else {
      window.open('https://apps.apple.com/app/odyvoy', '_blank');
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {children}
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-64 bg-popover/95 backdrop-blur-sm border border-border shadow-xl z-50"
        >
          <DropdownMenuItem 
            onClick={() => setIsAccountOpen(true)}
            className="cursor-pointer hover:bg-accent/50 transition-colors py-3"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5" />
                <span className="font-medium">Voyager</span>
              </div>
              <ExternalLink className="h-4 w-4 opacity-60" />
            </div>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem 
            onClick={() => setIsSubscriptionOpen(true)}
            className="cursor-pointer hover:bg-accent/50 transition-colors py-3"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <Crown className="h-5 w-5" />
                <span className="font-medium">Upgrade to Premium</span>
              </div>
              <ExternalLink className="h-4 w-4 opacity-60" />
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem 
            onClick={() => setIsPaymentsOpen(true)}
            className="cursor-pointer hover:bg-accent/50 transition-colors py-3"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5" />
                <span className="font-medium">Payments</span>
              </div>
              <ExternalLink className="h-4 w-4 opacity-60" />
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem 
            onClick={() => setIsSupportOpen(true)}
            className="cursor-pointer hover:bg-accent/50 transition-colors py-3"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <HelpCircle className="h-5 w-5" />
                <span className="font-medium">Support</span>
              </div>
              <ExternalLink className="h-4 w-4 opacity-60" />
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem 
            onClick={handleDownloadApp}
            className="cursor-pointer hover:bg-accent/50 transition-colors py-3"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <Download className="h-5 w-5" />
                <span className="font-medium">Download</span>
              </div>
              <ExternalLink className="h-4 w-4 opacity-60" />
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem 
            onClick={() => navigate('/referral')}
            className="cursor-pointer hover:bg-accent/50 transition-colors py-3"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <Gift className="h-5 w-5" />
                <span className="font-medium">Referral Program</span>
              </div>
              <ExternalLink className="h-4 w-4 opacity-60" />
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem 
            onClick={() => navigate('/blog')}
            className="cursor-pointer hover:bg-accent/50 transition-colors py-3"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <BookOpen className="h-5 w-5" />
                <span className="font-medium">Blog</span>
              </div>
              <ExternalLink className="h-4 w-4 opacity-60" />
            </div>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem 
            onClick={handleSignOut}
            className="cursor-pointer hover:bg-accent/50 transition-colors py-3 text-destructive hover:text-destructive"
          >
            <div className="flex items-center gap-3">
              <LogOut className="h-5 w-5" />
              <span className="font-medium">Log out</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Account Dialog */}
      <Dialog open={isAccountOpen} onOpenChange={setIsAccountOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Voyager Settings</DialogTitle>
          </DialogHeader>
          <AccountSection />
        </DialogContent>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={isSubscriptionOpen} onOpenChange={setIsSubscriptionOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Subscription Management</DialogTitle>
          </DialogHeader>
          <SubscriptionSection />
        </DialogContent>
      </Dialog>

      {/* Support Dialog */}
      <Dialog open={isSupportOpen} onOpenChange={setIsSupportOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Support Center</DialogTitle>
          </DialogHeader>
          <HelpSection />
        </DialogContent>
      </Dialog>

      {/* Payments Dialog */}
      <Dialog open={isPaymentsOpen} onOpenChange={setIsPaymentsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payments</DialogTitle>
          </DialogHeader>
          <PaymentSection />
        </DialogContent>
      </Dialog>

      <LiveChatModal 
        open={isChatOpen} 
        onOpenChange={setIsChatOpen} 
      />
      
      <EmailSupportModal 
        open={isEmailOpen} 
        onOpenChange={setIsEmailOpen} 
      />
    </>
  );
}